/**
 * Push-Daten → Ziel-Route (Marcel 2026-10-24): 1:1-Port der App-Routentabelle
 * dartcloud-frontend/lib/core/helper/push_route.dart (PURE). Gleiche Fälle in
 * src/utils/pushRoute.test.ts; Parität gegen dartcloud-backend/tests/fixtures/push-routes.json.
 *
 * Genutzt vom Posteingang „Neu für dich“ (ForYouInbox): jeder Eintrag trägt die
 * Push-Daten (`data`), daraus ergibt sich die Seite, die beim Klick geöffnet wird.
 *
 * Regeln:
 * 1. Online-Darts-Pushes zuerst.
 * 2. Bekannte Typen → feste Zielseite; fehlt ID/Slug → passende Übersicht.
 * 3. Listing-Pushes: Backend-Typ ('tournament', 'club', …) UND Web-Pfadwort ('turnier', 'verein', …).
 * 4. Expliziter Pfad (`path`/`route`, dartcloudapp.de-`url`/`link`) → genau dieser Pfad.
 * 5. Unbekannt/leer → null.
 */

export interface PushTarget {
  route: string;
  arguments?: Record<string, unknown>;
}

const str = (v: unknown): string | null => {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length === 0 || s === 'null' ? null : s;
};

const enc = (v: string): string => encodeURIComponent(v);

/** Listing-Typ (Backend-Schlüssel ODER Web-Pfadwort) → Pfadwort */
export const listingPushPaths: Record<string, string> = {
  tournament: 'turnier',
  turnier: 'turnier',
  series: 'turnierserie',
  turnierserie: 'turnierserie',
  serie: 'turnierserie',
  club: 'verein',
  verein: 'verein',
  pub: 'spielstaette',
  spielstaette: 'spielstaette',
  shop: 'shop',
  blog: 'blog',
  league: 'liga',
  liga: 'liga',
  association: 'verband',
  verband: 'verband',
};

/** Online-Darts (Port von online_match_view.dart → onlinePushRoute) */
export function onlinePushRoute(data: Record<string, unknown>): string | null {
  const type = str(data.type);
  const id = str(data.matchId);
  switch (type) {
    case 'ONLINE_CHALLENGE':
    case 'ONLINE_MATCH_READY':
    case 'ONLINE_MATCH_STARTED':
    case 'ONLINE_MATCH_FINISHED':
    case 'ONLINE_OBJECTION':
      return id ? `/online/spiel/${enc(id)}` : '/online';
    case 'ONLINE_QUICK_WAITING':
      return '/online';
    default:
      return null;
  }
}

/** Expliziter Pfad: `path`/`route` (absolut) oder dartcloudapp.de-URL (`url`/`link`) → Pfad; sonst null */
/** Pfad mit Segment „null“/„undefined“ (z. B. /turnier/x/spiel/null aus einer alten Rundnachricht) ist kaputt → typisierte Ableitung (26.10.2026) */
export const brokenPushPath = (path: string): boolean => /(^|\/)(null|undefined)(\/|\?|$)/.test(path);

export function pushPathOf(data: Record<string, unknown>): string | null {
  for (const key of ['path', 'route']) {
    const v = str(data[key]);
    if (v && v.startsWith('/') && !v.startsWith('//') && !brokenPushPath(v)) return v;
  }
  for (const key of ['url', 'link']) {
    const v = str(data[key]);
    if (!v) continue;
    let uri: URL | null = null;
    try { uri = new URL(v); } catch { uri = null; }
    if (!uri) continue;
    const host = uri.hostname.toLowerCase();
    if ((host === 'dartcloudapp.de' || host === 'www.dartcloudapp.de') && uri.pathname && uri.pathname !== '/') {
      return uri.search ? `${uri.pathname}${uri.search}` : uri.pathname;
    }
  }
  return null;
}

/** Ziel-Route für Push-Daten (`data` eines Posteingangs-Eintrags). null = kein Ziel. */
export function pushTargetFor(data: Record<string, unknown> | null | undefined): PushTarget | null {
  if (!data || typeof data !== 'object' || Object.keys(data).length === 0) return null;
  const type = str(data.type);
  const slug = str(data.slug);
  const id = str(data.id);

  const online = onlinePushRoute(data);
  if (online) return { route: online };

  const explicit = pushPathOf(data);
  if (explicit) return { route: explicit };

  switch (type) {
    case 'CHAT_MESSAGE': {
      const chatId = str(data.chatId);
      return { route: chatId ? `/nachrichten/${enc(chatId)}` : '/nachrichten' };
    }
    case 'prediction_game':
      return { route: slug ? `/tippspiele/${enc(slug)}` : '/tippspiele' };
    case 'news_article':
    case 'news':
    case 'news-article':
      return { route: slug ? `/news/${enc(slug)}` : '/news' };
    case 'post':
      return { route: id ? `/beitrag/${enc(id)}` : '/' };
    case 'dart-room':
      return { route: id ? `/dart-room/${enc(id)}` : '/community' };
    case 'setup':
      return { route: id ? `/setup/${enc(id)}` : '/setups' };
    case 'live-chat':
      return { route: '/' };
    case 'pro-event':
      return { route: id ? `/spiel/${enc(id)}` : '/' };
    // Online-Turnier (26.10.2026): Paarungsseite; ohne Spiel-Id Turnierseite (wie App)
    case 'ONLINE_TOURNAMENT_MATCH': {
      const matchId = str(data.matchId);
      return { route: slug && matchId ? `/turnier/${enc(slug)}/spiel/${enc(matchId)}` : slug ? `/turnier/${enc(slug)}` : '/turniere' };
    }
    case 'new_registration': {
      const tournamentId = str(data.tournamentId);
      return { route: tournamentId ? `/verwaltung/turnier/${enc(tournamentId)}/anmeldungen` : '/verwaltung' };
    }
    case 'registration_confirmed':
    case 'registration_rejected':
    case 'payment_received':
      return { route: '/meine-anmeldungen' };
    case 'forum_reply':
    case 'forum_mention':
    case 'forum_subscription': {
      const topicSlug = str(data.topicSlug);
      return { route: topicSlug ? `/forum/topic/${enc(topicSlug)}` : '/forum' };
    }
    case 'forum_upvote':
      return { route: '/forum' };
    case 'follow': {
      const username = str(data.username);
      return { route: username ? `/spieler/${enc(username)}` : '/profil' };
    }
    case 'listing_follow':
      return { route: '/verwaltung' };
    case 'ranking_link_request':
      return { route: '/verwaltung' };
    case 'ranking_link_decision':
      return { route: '/profil' };
    case 'contact_request':
      return { route: '/kontakt' };
    case 'invite_registered':
    case 'invite_qualified':
    case 'invite_bonus':
      return { route: '/profil/einladen' };
    case 'tournaments-nearby':
      return { route: '/in-der-naehe' };
    case 'listing-broadcast':
    case 'listing-stale-reminder': {
      const listingType = str(data.listingType);
      const listingSlug = str(data.listingSlug);
      const path = listingType ? listingPushPaths[listingType] : undefined;
      if (path && listingSlug) return { route: `/${path}/${enc(listingSlug)}` };
      return { route: type === 'listing-stale-reminder' ? '/verwaltung' : '/' };
    }
    default:
      break;
  }

  const listingPath = type ? listingPushPaths[type] : undefined;
  if (listingPath) {
    const s = slug ?? str(data.listingSlug);
    if (!s) return null;
    return { route: `/${listingPath}/${enc(s)}`, arguments: { view: 'intern' } };
  }

  return null;
}

export default pushTargetFor;
